import { isFirebaseConfigured } from "@/auth/firebase";
import { callGetLeaderboard, callGmGrantLeaderboardStat } from "@/cloud/game-callables";
import { grantExpForUser, resetProgressionForUser } from "@/game/model/grant-exp";
import {
  loadCharacterProgression,
  progressionXpDisplay,
} from "@/game/model/character-progression";
import { getGameSettings } from "@/game/model/game-settings";
import { normalizePartKind } from "@/home/part-kind";
import type { HomeWallet, WalletGrant } from "@/home/economy";
import type { HomePartDefinition, HomeRoomData } from "@/home/storage";
import {
  LEADERBOARD_BOARD_LABELS,
  type LeaderboardBoard,
} from "@/rank/leaderboard-types";

export type GmHomeToolsContext = {
  userId: string;
  getHomeData: () => HomeRoomData;
  getSelectedPartId: () => string | null;
  getWallet: () => HomeWallet;
  grantWallet: (grant: WalletGrant) => void;
  onHomeDataMutated: () => void;
  onExpGranted: () => void;
};

type RankRow = { value: number; rank: number | null };

const RANK_QUICK_AMOUNTS = [100, 500, 1000, 5000];
const WALLET_QUICK = {
  coins: [1000, 10000, 100000],
  gems: [100, 500, 2000],
  cash: [500, 2000, 10000],
} as const;

function formatRankLine(board: LeaderboardBoard, row: RankRow | null): string {
  if (!row) return "—";
  const label = LEADERBOARD_BOARD_LABELS[board];
  const rankText = row.rank != null ? `#${row.rank}` : "—";
  return `${label} ${row.value.toLocaleString("en-US")} · อันดับ ${rankText}`;
}

function grantRowHtml(
  idPrefix: string,
  label: string,
  icon: string,
  defaultValue: number,
  quick: readonly number[],
): string {
  const chips = quick
    .map(
      (n) =>
        `<button type="button" class="gm-chip" data-gm-quick="${idPrefix}" data-amount="${n}">+${n.toLocaleString("en-US")}</button>`,
    )
    .join("");
  return `
    <div class="gm-grant-row">
      <div class="gm-grant-row-head">
        <span class="gm-grant-icon" aria-hidden="true">${icon}</span>
        <span class="gm-grant-label">${label}</span>
      </div>
      <div class="gm-tools-row">
        <input type="number" id="gm-grant-${idPrefix}" class="input gm-input-compact" min="1" step="1" value="${defaultValue}" />
        <button type="button" class="btn gm-btn-give" id="gm-grant-${idPrefix}-btn">ให้</button>
      </div>
      <div class="gm-chip-row">${chips}</div>
    </div>
  `;
}

export function mountGmHomeTools(
  host: HTMLElement,
  ctx: GmHomeToolsContext,
): { refresh: () => void; teardown: () => void } {
  let rankRows: Partial<Record<LeaderboardBoard, RankRow>> = {};
  let rankLoading = false;
  let rankStatus = "";

  const refreshRankStats = async () => {
    if (!isFirebaseConfigured()) {
      rankStatus = "ยังไม่เชื่อม Firebase";
      updateRankDisplay();
      return;
    }
    rankLoading = true;
    rankStatus = "โหลดแรงค์…";
    updateRankDisplay();
    try {
      const boards: LeaderboardBoard[] = ["raid", "tower", "wealth"];
      const results = await Promise.all(boards.map((b) => callGetLeaderboard(b)));
      rankRows = {};
      for (const res of results) {
        const me = res.me;
        rankRows[res.board] = me
          ? { value: me.value, rank: me.rank }
          : { value: 0, rank: null };
      }
      rankStatus = "";
    } catch (err) {
      rankStatus = err instanceof Error ? err.message : "โหลดแรงค์ไม่สำเร็จ";
    } finally {
      rankLoading = false;
      updateRankDisplay();
    }
  };

  const updateRankDisplay = () => {
    const statusEl = host.querySelector<HTMLElement>("#gm-rank-status");
    if (statusEl) {
      statusEl.textContent = rankStatus;
      statusEl.classList.toggle("gm-tools-status--error", Boolean(rankStatus && !rankLoading));
    }
    const raidEl = host.querySelector<HTMLElement>("#gm-rank-raid-line");
    const towerEl = host.querySelector<HTMLElement>("#gm-rank-tower-line");
    const wealthEl = host.querySelector<HTMLElement>("#gm-rank-wealth-line");
    if (raidEl) raidEl.textContent = formatRankLine("raid", rankRows.raid ?? null);
    if (towerEl) towerEl.textContent = formatRankLine("tower", rankRows.tower ?? null);
    if (wealthEl) wealthEl.textContent = formatRankLine("wealth", rankRows.wealth ?? null);
    const refreshBtn = host.querySelector<HTMLButtonElement>("#gm-rank-refresh");
    if (refreshBtn) refreshBtn.disabled = rankLoading;
  };

  const render = () => {
    const defaults = getGameSettings();
    const prog = loadCharacterProgression(ctx.userId);
    const xp = progressionXpDisplay(prog);
    const homeData = ctx.getHomeData();
    const partId = ctx.getSelectedPartId();
    const part = partId ? homeData.parts.find((p) => p.id === partId) : null;
    const isMonster = part && normalizePartKind(part.kind) === "monster";
    const wallet = ctx.getWallet();
    const xpPct =
      xp.xpToNext > 0 ? Math.min(100, Math.round((xp.xpIntoLevel / xp.xpToNext) * 100)) : 100;

    host.innerHTML = `
      <section class="gm-tools-panel" aria-label="เครื่องมือ GM">
        <header class="gm-tools-header">
          <div class="gm-tools-header-brand">
            <span class="gm-tools-badge">GM</span>
            <span class="gm-tools-header-title">คอนโซล</span>
          </div>
          <p class="gm-tools-header-tag">โหมดทดสอบ · ไม่โชว์ผู้เล่นทั่วไป</p>
        </header>

        <article class="gm-tools-card">
          <h4 class="gm-tools-card-title"><span class="gm-tools-card-icon">⬆</span> เลเวล &amp; EXP</h4>
          <div class="gm-stat-pill">Lv.<strong>${prog.level}</strong></div>
          <div class="gm-xp-bar" role="progressbar" aria-valuenow="${xpPct}" aria-valuemin="0" aria-valuemax="100">
            <div class="gm-xp-bar-fill" style="width:${xpPct}%"></div>
          </div>
          <p class="gm-tools-meta">EXP ${prog.totalExp.toLocaleString("en-US")} · ในเลเวล ${xp.xpIntoLevel}/${xp.xpToNext || "—"}</p>
          ${grantRowHtml("exp", "ให้ EXP", "✦", 1000, [500, 1000, 5000])}
          <button type="button" class="btn gm-btn-danger-block" id="gm-reset-level-btn">รีเซ็ตเลเวล → Lv.1</button>
        </article>

        <article class="gm-tools-card gm-tools-card--rank">
          <div class="gm-tools-card-head-row">
            <h4 class="gm-tools-card-title"><span class="gm-tools-card-icon">🏆</span> แรงค์โลก</h4>
            <button type="button" class="btn btn-ghost btn-xs" id="gm-rank-refresh" title="โหลดใหม่">↻</button>
          </div>
          <ul class="gm-rank-lines">
            <li id="gm-rank-raid-line">${formatRankLine("raid", rankRows.raid ?? null)}</li>
            <li id="gm-rank-tower-line">${formatRankLine("tower", rankRows.tower ?? null)}</li>
            <li id="gm-rank-wealth-line">${formatRankLine("wealth", rankRows.wealth ?? null)}</li>
          </ul>
          <p class="gm-tools-meta" id="gm-rank-status" role="status">${rankStatus}</p>
          ${grantRowHtml("rank-raid", "คะแนนต่อสู้ (Raid)", "⚔", 500, RANK_QUICK_AMOUNTS)}
          <div class="gm-grant-row">
            <div class="gm-grant-row-head">
              <span class="gm-grant-icon" aria-hidden="true">▲</span>
              <span class="gm-grant-label">ชั้นหอคอย (ตั้งค่า)</span>
            </div>
            <div class="gm-tools-row">
              <input type="number" id="gm-grant-rank-tower" class="input gm-input-compact" min="1" max="100" step="1" value="10" />
              <button type="button" class="btn gm-btn-give" id="gm-grant-rank-tower-btn">ตั้ง</button>
            </div>
          </div>
          ${grantRowHtml("rank-wealth", "คะแนนรำรวย (เซิร์ฟ)", "◎", 10000, [10000, 100000, 1000000])}
        </article>

        <article class="gm-tools-card">
          <h4 class="gm-tools-card-title"><span class="gm-tools-card-icon">💰</span> ทรัพยากรในเครื่อง</h4>
          <div class="gm-wallet-strip">
            <span class="gm-wallet-chip gm-wallet-chip--gold">🪙 ${wallet.coins.toLocaleString("en-US")}</span>
            <span class="gm-wallet-chip gm-wallet-chip--gem">💎 ${wallet.gems.toLocaleString("en-US")}</span>
            <span class="gm-wallet-chip gm-wallet-chip--cash">💵 ${wallet.cash.toLocaleString("en-US")}</span>
          </div>
          ${grantRowHtml("coins", "ทอง", "🪙", 10000, WALLET_QUICK.coins)}
          ${grantRowHtml("gems", "เพชร", "💎", 500, WALLET_QUICK.gems)}
          ${grantRowHtml("cash", "Cash", "💵", 1000, WALLET_QUICK.cash)}
        </article>

        ${
          isMonster && part
            ? `
        <article class="gm-tools-card">
          <h4 class="gm-tools-card-title"><span class="gm-tools-card-icon">👾</span> มอนสเตอร์ที่เลือก</h4>
          <p class="gm-tools-meta"><strong>${part.name}</strong></p>
          <div class="gm-monster-grid">
            <label class="field"><span class="field-label">Lv</span>
              <input type="number" class="input gm-monster-field" data-field="monsterLevel" min="1" max="100" value="${part.monsterLevel ?? defaults.monsterDefaultLevel}" /></label>
            <label class="field"><span class="field-label">HP</span>
              <input type="number" class="input gm-monster-field" data-field="monsterBaseHp" min="1" value="${part.monsterBaseHp ?? defaults.monsterDefaultHp}" /></label>
            <label class="field"><span class="field-label">ATK</span>
              <input type="number" class="input gm-monster-field" data-field="monsterBaseAttack" min="1" value="${part.monsterBaseAttack ?? defaults.monsterDefaultAttack}" /></label>
            <label class="field"><span class="field-label">DEF</span>
              <input type="number" class="input gm-monster-field" data-field="monsterBaseDefense" min="0" value="${part.monsterBaseDefense ?? defaults.monsterDefaultDefense}" /></label>
            <label class="field"><span class="field-label">หลบ</span>
              <input type="number" class="input gm-monster-field" data-field="monsterBaseDodge" min="0" value="${part.monsterBaseDodge ?? defaults.monsterDefaultDodge}" /></label>
            <label class="field"><span class="field-label">EXP</span>
              <input type="number" class="input gm-monster-field" data-field="monsterXpReward" min="0" value="${part.monsterXpReward ?? defaults.monsterDefaultXpReward}" /></label>
          </div>
          <button type="button" class="btn btn-ghost gm-btn-block" id="gm-save-monster">บันทึกสเตตมอนสเตอร์</button>
        </article>
        `
            : `
        <p class="gm-tools-foot-hint">เลือกมอนสเตอร์ในโหมดแต่งบ้านเพื่อแก้สเตต</p>
        `
        }
      </section>
    `;
    updateRankDisplay();
  };

  const grantFromInput = (inputId: string, field: keyof WalletGrant) => {
    const input = host.querySelector<HTMLInputElement>(`#${inputId}`);
    const amount = Math.floor(Number(input?.value ?? 0));
    if (amount <= 0) return;
    ctx.grantWallet({ [field]: amount });
    render();
  };

  const grantRank = async (board: LeaderboardBoard, amount: number) => {
    if (!isFirebaseConfigured()) {
      rankStatus = "ต้องตั้งค่า Firebase ก่อน";
      updateRankDisplay();
      return;
    }
    rankLoading = true;
    rankStatus = "กำลังให้คะแนน…";
    updateRankDisplay();
    try {
      const res = await callGmGrantLeaderboardStat({ board, amount });
      rankRows[board] = {
        value: res.value,
        rank: rankRows[board]?.rank ?? null,
      };
      rankStatus = `อัปเดต ${LEADERBOARD_BOARD_LABELS[board]} แล้ว`;
      void refreshRankStats();
    } catch (err) {
      rankStatus = err instanceof Error ? err.message : "ให้คะแนนไม่สำเร็จ";
    } finally {
      rankLoading = false;
      updateRankDisplay();
    }
  };

  const onHostClick = (event: Event) => {
    const target = event.target as HTMLElement;

    const quick = target.closest<HTMLElement>("[data-gm-quick]");
    if (quick) {
      const key = quick.dataset.gmQuick;
      const amount = Math.floor(Number(quick.dataset.amount ?? 0));
      if (!key || amount <= 0) return;
      const input = host.querySelector<HTMLInputElement>(`#gm-grant-${key}`);
      if (input) input.value = String(amount);
      if (key === "rank-raid") {
        void grantRank("raid", amount);
        return;
      }
      if (key === "rank-wealth") {
        void grantRank("wealth", amount);
        return;
      }
      if (key === "coins") grantFromInput("gm-grant-coins", "coins");
      if (key === "gems") grantFromInput("gm-grant-gems", "gems");
      if (key === "cash") grantFromInput("gm-grant-cash", "cash");
      if (key === "exp") {
        grantExpForUser(ctx.userId, amount);
        ctx.onExpGranted();
        render();
      }
      return;
    }

    if (target.id === "gm-grant-coins-btn" || target.closest("#gm-grant-coins-btn")) {
      grantFromInput("gm-grant-coins", "coins");
      return;
    }
    if (target.id === "gm-grant-gems-btn" || target.closest("#gm-grant-gems-btn")) {
      grantFromInput("gm-grant-gems", "gems");
      return;
    }
    if (target.id === "gm-grant-cash-btn" || target.closest("#gm-grant-cash-btn")) {
      grantFromInput("gm-grant-cash", "cash");
      return;
    }
    if (target.id === "gm-grant-exp-btn" || target.closest("#gm-grant-exp-btn")) {
      const input = host.querySelector<HTMLInputElement>("#gm-grant-exp");
      const amount = Math.floor(Number(input?.value ?? 0));
      if (amount <= 0) return;
      grantExpForUser(ctx.userId, amount);
      ctx.onExpGranted();
      render();
      return;
    }
    if (target.id === "gm-grant-rank-raid-btn" || target.closest("#gm-grant-rank-raid-btn")) {
      const input = host.querySelector<HTMLInputElement>("#gm-grant-rank-raid");
      const amount = Math.floor(Number(input?.value ?? 0));
      if (amount <= 0) return;
      void grantRank("raid", amount);
      return;
    }
    if (target.id === "gm-grant-rank-tower-btn" || target.closest("#gm-grant-rank-tower-btn")) {
      const input = host.querySelector<HTMLInputElement>("#gm-grant-rank-tower");
      const amount = Math.floor(Number(input?.value ?? 0));
      if (amount <= 0) return;
      void grantRank("tower", amount);
      return;
    }
    if (target.id === "gm-grant-rank-wealth-btn" || target.closest("#gm-grant-rank-wealth-btn")) {
      const input = host.querySelector<HTMLInputElement>("#gm-grant-rank-wealth");
      const amount = Math.floor(Number(input?.value ?? 0));
      if (amount <= 0) return;
      void grantRank("wealth", amount);
      return;
    }
    if (target.id === "gm-rank-refresh" || target.closest("#gm-rank-refresh")) {
      void refreshRankStats();
      return;
    }
    if (target.id === "gm-reset-level-btn" || target.closest("#gm-reset-level-btn")) {
      const prog = loadCharacterProgression(ctx.userId);
      if (
        !window.confirm(
          `รีเซ็ตเลเวลของคุณ?\n\nLv.${prog.level} → Lv.1, EXP และสเตตกลับค่าเริ่มต้น`,
        )
      ) {
        return;
      }
      resetProgressionForUser(ctx.userId);
      ctx.onExpGranted();
      render();
      return;
    }
    if (target.id === "gm-save-monster" || target.closest("#gm-save-monster")) {
      const homeData = ctx.getHomeData();
      const partId = ctx.getSelectedPartId();
      if (!partId) return;
      const idx = homeData.parts.findIndex((p) => p.id === partId);
      if (idx < 0) return;
      const next: HomePartDefinition = { ...homeData.parts[idx] };
      const fields = host.querySelectorAll<HTMLInputElement>(".gm-monster-field");
      for (const el of fields) {
        const key = el.dataset.field;
        if (!key) continue;
        const n = Math.floor(Number(el.value));
        if (!Number.isFinite(n)) continue;
        switch (key) {
          case "monsterLevel":
            next.monsterLevel = n;
            break;
          case "monsterBaseHp":
            next.monsterBaseHp = n;
            break;
          case "monsterBaseAttack":
            next.monsterBaseAttack = n;
            break;
          case "monsterBaseDefense":
            next.monsterBaseDefense = n;
            break;
          case "monsterBaseDodge":
            next.monsterBaseDodge = n;
            break;
          case "monsterXpReward":
            next.monsterXpReward = n;
            break;
          default:
            break;
        }
      }
      homeData.parts[idx] = next;
      ctx.onHomeDataMutated();
      render();
    }
  };

  host.addEventListener("click", onHostClick);
  render();
  void refreshRankStats();

  return {
    refresh: () => {
      render();
      void refreshRankStats();
    },
    teardown: () => {
      host.removeEventListener("click", onHostClick);
      host.innerHTML = "";
    },
  };
}
